/**
 * exportImage.js — Direct DOM capture with html-to-image
 *
 * Architecture: "Direct Ref Capture"
 *   1. Caller passes a DOM element (ref.current) already rendered on screen
 *   2. toPng() clones the node, inlines computed styles, and renders to PNG
 *   3. Download triggered via temporary <a> link
 *
 * Based on the /dexbuilder reference implementation.
 */
import { toPng, toBlob } from 'html-to-image';

/**
 * Generate filename with YYYY-MM-DD timestamp.
 */
export function getExportFileName(teamName) {
  const date = new Date().toISOString().slice(0, 10);
  const cleanName = (teamName || 'team')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9-_]/g, '');
  return `${cleanName}-team_${date}.png`;
}

/**
 * Export a DOM element as a PNG image download.
 *
 * @param {HTMLElement} element  - The DOM node to capture
 * @param {string}      teamName - Used for the download filename
 * @returns {Promise<void>}
 */
export async function exportTeamAsImage(element, teamName) {
  if (!element) throw new Error('Export element not found');

  // Small delay to let any pending renders/images settle
  await new Promise(resolve => setTimeout(resolve, 150));

  const dataUrl = await toPng(element, {
    cacheBust: true,
    pixelRatio: 2,
    style: {
      borderRadius: '0',
    },
    filter: (node) => {
      // Exclude any nodes with data-export-ignore attribute
      if (node?.dataset?.exportIgnore) return false;
      return true;
    },
  });

  const link = document.createElement('a');
  link.download = getExportFileName(teamName);
  link.href = dataUrl;
  link.click();
}

/**
 * Get image as Blob (without downloading).
 *
 * @param {HTMLElement} element
 * @returns {Promise<Blob>}
 */
export async function getTeamImageBlob(element) {
  if (!element) throw new Error('Export element not found');

  await new Promise(resolve => setTimeout(resolve, 150));

  const blob = await toBlob(element, {
    cacheBust: true,
    pixelRatio: 2,
    style: {
      borderRadius: '0',
    },
  });

  if (!blob || blob.size === 0) {
    throw new Error('Empty PNG blob');
  }

  return blob;
}
